import { useCallback, useEffect, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** How far from the bottom counts as "away from it".
 *
 * Generous on purpose: a few lines of drift while reading the last reply is not
 * being lost, and a button that appears every time you nudge the wheel is
 * noise. Roughly a screenful of a short reply. */
const AWAY_FROM_BOTTOM_PX = 240;

/**
 * A way back down, for when you have scrolled up.
 *
 * The transcript pins itself to the bottom for new turns and, while streaming,
 * only if you are already near it — deliberately, so scrolling up to re-read
 * something is not yanked away mid-sentence. The cost of that is the obvious
 * one: having scrolled up, the only way back was to scroll, and a long
 * conversation is a long scroll.
 *
 * Listens through `addEventListener` rather than an `onScroll` prop, because
 * both surfaces already have their own scroll handler for loading older
 * messages, and a second prop would mean editing theirs. This attaches
 * alongside and leaves them untouched.
 */
export function ScrollToBottom({
  containerRef,
  className,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  className?: string;
}) {
  const [away, setAway] = useState(false);

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAway(distance > AWAY_FROM_BOTTOM_PX);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Content growing counts as moving away from the bottom just as much as
    // scrolling does: a reply streaming in while you are reading further up
    // should raise the button, and no scroll event fires for that.
    const observer = new ResizeObserver(measure);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [containerRef, measure]);

  if (!away) return null;

  return (
    <button
      type="button"
      onClick={() => {
        const el = containerRef.current;
        el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }}
      aria-label="Scroll to the latest message"
      title="Scroll to the latest message"
      className={cn(
        'absolute left-1/2 z-20 -translate-x-1/2 rounded-full border border-border bg-card p-2 text-muted-foreground shadow-md transition-colors hover:bg-accent hover:text-accent-foreground',
        className,
      )}
    >
      <ArrowDown className="h-4 w-4" />
    </button>
  );
}
